import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, WarningCircle, CheckCircle } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortalActivate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ phase: "verifying" });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setState({ phase: "invalid", reason: "missing_token" }); return; }
    fetch(`${API}/api/portal/verify-token?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.valid) setState({ phase: "ready", email: d.email, kind: d.kind });
        else setState({ phase: "invalid", reason: d.reason });
      })
      .catch(() => setState({ phase: "invalid", reason: "network" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setError("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/portal/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: pw }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d?.detail || "Failed to set password."); return; }
      setState({ phase: "done", kind: d.kind });
    } catch (ex) {
      setError("Network error. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="portal-activate-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl shadow-sm p-7">
        <div className="flex items-center gap-2 text-sky-700 mb-3">
          <ShieldCheck size={22} weight="fill" />
          <span className="text-lg font-semibold">KGS Candidate Portal</span>
        </div>

        {state.phase === "verifying" && (
          <div className="text-sm text-slate-500" data-testid="portal-verifying">Verifying your link…</div>
        )}

        {state.phase === "invalid" && (
          <div className="space-y-3" data-testid="portal-invalid">
            <div className="flex items-start gap-2 text-red-600">
              <WarningCircle size={20} weight="fill" />
              <div>
                <div className="font-semibold">This link is not valid</div>
                <div className="text-sm text-slate-600 mt-1">
                  {state.reason === "expired"
                    ? "The link has expired. Please contact your service delivery team to get a new one."
                    : "This link is invalid or has already been used. Please request a new activation email."}
                </div>
              </div>
            </div>
          </div>
        )}

        {state.phase === "ready" && (
          <form onSubmit={submit} className="space-y-3">
            <h1 className="text-xl font-semibold">
              {state.kind === "activation" ? "Set your password" : "Choose a new password"}
            </h1>
            <p className="text-sm text-slate-500">
              For <b>{state.email}</b>. Minimum 8 characters. Do not reuse a previous password.
            </p>
            <input type="password" required placeholder="New password"
                   data-testid="portal-new-password" value={pw} onChange={(e) => setPw(e.target.value)}
                   className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
            <input type="password" required placeholder="Confirm new password"
                   data-testid="portal-confirm-password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                   className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
            {error && <div className="text-xs text-red-600" data-testid="portal-error">{error}</div>}
            <button type="submit" disabled={submitting}
                    data-testid="portal-set-password-submit"
                    className="w-full bg-sky-600 hover:bg-sky-700 disabled:bg-slate-300 text-white text-sm font-medium py-2.5 rounded-md">
              {submitting ? "Saving…" : (state.kind === "activation" ? "Activate my account" : "Reset password")}
            </button>
          </form>
        )}

        {state.phase === "done" && (
          <div className="space-y-4" data-testid="portal-done">
            <div className="flex items-start gap-2 text-emerald-700">
              <CheckCircle size={22} weight="fill" />
              <div>
                <div className="font-semibold">
                  {state.kind === "activation" ? "Account activated" : "Password updated"}
                </div>
                <div className="text-sm text-slate-600 mt-1">You can now log in to the candidate portal.</div>
              </div>
            </div>
            <Link to="/portal" data-testid="portal-goto-login"
                  className="block text-center w-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium py-2.5 rounded-md">
              Go to portal login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
